import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { HotkeysProvider } from './contexts/HotkeysContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { RegimeProvider } from './contexts/RegimeContext';
import { NewLayout } from './components/layout/NewLayout';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import { trackPageView } from './lib/metaPixel';

// Marketing (public)
const LandingPage = lazy(() => import('./pages/marketing/LandingPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/marketing/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/marketing/TermsOfServicePage'));

// Auth Pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const CompanySelectPage = lazy(() => import('./pages/auth/CompanySelectPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const AcceptInvitePage = lazy(() => import('./pages/invite/AcceptInvitePage'));

// App Pages
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const PayablesPage = lazy(() => import('./pages/payables/PayablesPage'));
const ReceivablesPage = lazy(() => import('./pages/receivables/ReceivablesPage'));
const CashFlowPage = lazy(() => import('./pages/cashflow/CashFlowPage'));
const ReconciliationPage = lazy(() => import('./pages/reconciliation/ReconciliationPage'));
const ReconciliationMetricsPage = lazy(() => import('./pages/reconciliation/ReconciliationMetricsPage'));
const ReconciliationRulesPage = lazy(() => import('./pages/reconciliation-rules/ReconciliationRulesPage'));
const SalesPage = lazy(() => import('./pages/sales/SalesPage'));
const PurchasesPage = lazy(() => import('./pages/purchases/PurchasesPage'));
const ContractsPage = lazy(() => import('./pages/contracts/ContractsPage'));
const CreditCardsPage = lazy(() => import('./pages/credit-cards/CreditCardsPage'));
const EntitiesPage = lazy(() => import('./pages/entities/EntitiesPage'));
const AccountsPage = lazy(() => import('./pages/accounts/AccountsPage'));
const CategoriesPage = lazy(() => import('./pages/categories/CategoriesPage'));
const CostCentersPage = lazy(() => import('./pages/costcenters/CostCentersPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const CompaniesPage = lazy(() => import('./pages/companies/CompaniesPage'));
const AuditLogsPage = lazy(() => import('./pages/audit/AuditLogsPage'));
const OpenFinancePage = lazy(() => import('./pages/openfinance/OpenFinancePage'));
const BudgetPage = lazy(() => import('./pages/budget/BudgetPage'));
const AccountingPage = lazy(() => import('./pages/accounting/AccountingPage'));
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
const DeveloperPage = lazy(() => import('./pages/developer/DeveloperPage'));
const ProductsPage = lazy(() => import('./pages/products/ProductsPage'));
const ProductFormPage = lazy(() => import('./pages/products/ProductFormPage'));
const ServicesPage = lazy(() => import('./pages/services/ServicesPage'));
const ServiceFormPage = lazy(() => import('./pages/services/ServiceFormPage'));
const ServiceOrdersPage = lazy(() => import('./pages/services/ServiceOrdersPage'));
const IntegrationsHubPage = lazy(() => import('./pages/integrations/IntegrationsHubPage'));
const AutomationsPage = lazy(() => import('./pages/automations/AutomationsPage'));
const ExtractPage = lazy(() => import('./pages/financial/ExtractPage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const BillingSuccessPage = lazy(() => import('./pages/billing/BillingSuccessPage'));
const BillingCanceledPage = lazy(() => import('./pages/billing/BillingCanceledPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));

const SuspenseFallback = (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route Component for auth pages (redirect to dashboard if already authenticated)
function PublicAuthRoute({ children }) {
  const { isAuthenticated, isLoading, isLoginTransitioning } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isAuthenticated && !isLoginTransitioning) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();

  // Meta Pixel: dispara PageView a cada troca de rota da SPA.
  // Sem isso, a Meta só enxerga o carregamento inicial (o React Router
  // não recarrega a página, então o pixel nunca veria as outras telas).
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);

  // CRITICAL: Check URL fragment for session_id synchronously during render.
  // This must happen BEFORE ProtectedRoute runs to prevent race conditions.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={SuspenseFallback}>
      <Routes>
        {/* Public marketing landing — accessible to everyone, even authenticated */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacidade" element={<PrivacyPolicyPage />} />
        <Route path="/termos" element={<TermsOfServicePage />} />

        {/* Auth Routes — redirect to /dashboard if already logged in */}
        <Route path="/login" element={<PublicAuthRoute><LoginPage /></PublicAuthRoute>} />
        <Route path="/register" element={<PublicAuthRoute><RegisterPage /></PublicAuthRoute>} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />

        {/* Company Selection (after login if multiple companies) */}
        <Route path="/select-company" element={<ProtectedRoute><CompanySelectPage /></ProtectedRoute>} />

        {/* Protected ERP Routes — wrapped by NewLayout (pathless parent so children use absolute paths) */}
        <Route element={<ProtectedRoute><NewLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/payables" element={<PayablesPage />} />
          <Route path="/receivables" element={<ReceivablesPage />} />
          <Route path="/cash-flow" element={<CashFlowPage />} />
          <Route path="/reconciliation" element={<ReconciliationPage />} />
          <Route path="/reconciliation/metrics" element={<ReconciliationMetricsPage />} />
          <Route path="/reconciliation-rules" element={<ReconciliationRulesPage />} />
          <Route path="/open-finance" element={<OpenFinancePage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/purchases" element={<PurchasesPage />} />
          <Route path="/contracts" element={<ContractsPage />} />
          <Route path="/credit-cards" element={<CreditCardsPage />} />
          <Route path="/entities" element={<EntitiesPage />} />
          <Route path="/people" element={<Navigate to="/entities" replace />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/cost-centers" element={<CostCentersPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/audit-logs" element={<AuditLogsPage />} />
          <Route path="/budget" element={<BudgetPage />} />
          <Route path="/accounting" element={<AccountingPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/developer" element={<DeveloperPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/new" element={<ProductFormPage />} />
          <Route path="/products/:id" element={<ProductFormPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/new" element={<ServiceFormPage />} />
          <Route path="/services/:id" element={<ServiceFormPage />} />
          <Route path="/service-orders" element={<ServiceOrdersPage />} />
          <Route path="/integrations" element={<IntegrationsHubPage />} />
          <Route path="/automations" element={<AutomationsPage />} />
          <Route path="/bank-accounts" element={<AccountsPage />} />
          <Route path="/extract" element={<ExtractPage />} />
          <Route path="/financial-statement" element={<Navigate to="/extract" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/success" element={<BillingSuccessPage />} />
          <Route path="/billing/canceled" element={<BillingCanceledPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
        </Route>

        {/* Catch all — back to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <RegimeProvider>
            <SidebarProvider>
              <BrowserRouter>
                <HotkeysProvider>
                  <OnboardingProvider>
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                  </OnboardingProvider>
                </HotkeysProvider>
              </BrowserRouter>
            </SidebarProvider>
          </RegimeProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
